import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { addNetworkListener, removeNetworkListener, getNetworkQualityText } from '../../utils/networkMonitor';
import { getPendingReportsCount, addSyncListener, removeSyncListener } from '../../utils/backgroundSync';
import { addOfflineStorageListener, removeOfflineStorageListener } from '../../utils/offlineStorage';
import SyncProgress from './SyncProgress';
import styles from './OfflineIndicator.module.css';

/**
 * 오프라인 상태 및 동기화 표시 컴포넌트
 * - 네트워크 상태 실시간 표시
 * - 대기 중인 레포트 개수 표시
 * - 동기화 진행 상태 표시
 */
const OfflineIndicator = () => {
  const { t } = useTranslation();

  const [networkState, setNetworkState] = useState({
    isOnline: true,
    shouldUseOfflineMode: false,
    networkQuality: 'unknown'
  });

  const [syncState, setSyncState] = useState({
    isRunning: false,
    pendingCount: 0,
    progress: null,
    isRecovering: false,
    recoveryInfo: null
  });

  const [showSyncProgress, setShowSyncProgress] = useState(false);

  // 대기 중인 레포트 개수 업데이트
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await getPendingReportsCount();
      setSyncState(prevState => ({
        ...prevState,
        pendingCount: count
      }));
    } catch (error) {
      // console.error('[OfflineIndicator] 대기 레포트 개수 조회 실패:', error);
    }
  }, []);

  // 네트워크 상태 변화 핸들러
  const handleNetworkChange = useCallback((state) => {
    setNetworkState({
      isOnline: state.isOnline,
      shouldUseOfflineMode: state.shouldUseOfflineMode,
      networkQuality: getNetworkQualityText()
    });

    // 네트워크 상태 변화시 대기 레포트 개수 업데이트
    updatePendingCount();
  }, [updatePendingCount]);

  // 동기화 상태 변화 핸들러
  const handleSyncChange = useCallback((event, data) => {
    // console.log('[OfflineIndicator] 동기화 이벤트:', event, data);

    setSyncState(prevState => {
      const newState = { ...prevState };

      switch (event) {
        case 'auth_required':
          // 🆕 인증 필요 이벤트 처리
          // 사용자에게 명확한 알림 표시
          import('../../components/common/Alert').then(({ alert }) => {
            import('../../constants').then(({ ALERT_LEVEL }) => {
              alert(
                t ? t("Authentication required. Please login.") : "認証が必要です。ログインしてください。",
                ALERT_LEVEL.WARNING
              );
            });
          });
          // 2초 후 로그인 페이지로 이동
          setTimeout(() => {
            window.location.href = '/signIn';
          }, 2000);
          break;

        case 'sync_started':
          newState.isRunning = true;
          newState.progress = data;
          // 🆕 자동 동기화 시작 시 모달 자동 표시
          setShowSyncProgress(true);
          break;

        case 'sync_progress':
          newState.progress = data;
          break;

        case 'sync_completed':
        case 'sync_stopped':
          newState.isRunning = false;
          newState.progress = null;
          newState.isRecovering = false; // 복구 상태 정리
          newState.recoveryInfo = null;

          // 🔥 모바일 브라우저에서 애니메이션이 관성으로 계속 도는 문제 해결
          // CSS 애니메이션을 강제로 중단하고 재시작
          setTimeout(() => {
            const button = document.querySelector(`.${styles.floatingButton}`);
            if (button) {
              // 애니메이션 강제 리셋
              button.style.animation = 'none';
              // 리플로우 강제 트리거 (브라우저가 스타일 변경을 즉시 적용하도록)
              void button.offsetHeight;
              // 애니메이션 재적용 (rotating 제외된 상태로)
              button.style.animation = '';
            }
          }, 50);

          updatePendingCount(); // 동기화 완료 후 대기 개수 업데이트
          break;

        case 'report_synced':
          // 개별 레포트 동기화 완료시 대기 개수 업데이트
          if (data.success) {
            updatePendingCount();
          }
          break;

        case 'recovery_started':
          newState.isRecovering = true;
          newState.recoveryInfo = data;
          // 🆕 복구 시작 시에도 모달 자동 표시
          setShowSyncProgress(true);
          break;

        default:
          // 기본 케이스 추가
          break;
      }

      return newState;
    });
  }, [updatePendingCount]);

  // 🆕 오프라인 저장 이벤트 핸들러
  const handleOfflineStorageChange = useCallback((event, data) => {
    console.log('[OfflineIndicator] 오프라인 저장 이벤트:', event, data);

    if (event === 'report_saved_offline') {
      // 오프라인 저장 완료시 즉시 대기 개수 업데이트
      updatePendingCount();
    } else if (event === 'report_deleted_offline') {
      // 🆕 오프라인 레포트 삭제시 즉시 대기 개수 업데이트
      console.log('[OfflineIndicator] 오프라인 레포트 삭제됨 - 카운트 업데이트:', data.reportId);
      updatePendingCount();
    }
  }, [updatePendingCount]);

  // 컴포넌트 초기화
  useEffect(() => {
    // 네트워크 리스너 등록
    addNetworkListener(handleNetworkChange);

    // 동기화 리스너 등록
    addSyncListener(handleSyncChange);

    // 🆕 오프라인 저장 리스너 등록
    addOfflineStorageListener(handleOfflineStorageChange);

    // 초기 상태 설정
    updatePendingCount();

    return () => {
      removeNetworkListener(handleNetworkChange);
      removeSyncListener(handleSyncChange);
      removeOfflineStorageListener(handleOfflineStorageChange);
    };
  }, [handleNetworkChange, handleSyncChange, handleOfflineStorageChange, updatePendingCount]);

  // 플로팅 버튼 클릭 핸들러 (바로 모달 표시)
  const handleFloatingButtonClick = () => {
    // 동기화 중이든 아니든 항상 모달 표시
    setShowSyncProgress(true);
  };

  return (
    <>
      <div className={styles.container}>
        {/* 플로팅 버튼 (항상 표시, 데이터 없을 때는 회색) */}
        <div className={styles.floatingButtonWrapper}>
          {/* 플로팅 버튼 */}
          <button
            className={`${styles.floatingButton} ${
              syncState.isRunning ? styles.syncing :
              syncState.isRecovering ? styles.recovering :
              syncState.pendingCount > 0 ? styles.pending :
              styles.empty
            }`}
            onClick={handleFloatingButtonClick}
            title={
              syncState.isRunning ? t('Syncing in progress - Click to view') :
              syncState.isRecovering ? t('Recovering sync - Click to view') :
              syncState.pendingCount > 0 ? t('Click to view offline reports') :
              t('No offline reports')
            }
          >
            {syncState.isRunning || syncState.isRecovering ? (
              <>
                <span className={styles.buttonIcon}>🔄</span>
                <span className={styles.buttonBadge}>{syncState.progress?.completed || 0}/{syncState.progress?.total || syncState.pendingCount}</span>
              </>
            ) : (
              <>
                <span className={styles.buttonIcon}>📋</span>
                {syncState.pendingCount > 0 ? (
                  <span className={styles.buttonBadge}>{syncState.pendingCount}</span>
                ) : (
                  <span className={styles.buttonBadgeEmpty}>0</span>
                )}
              </>
            )}
          </button>
        </div>
      </div>

      {/* 동기화 진행률 모달 */}
      <SyncProgress
        isVisible={showSyncProgress}
        onClose={() => setShowSyncProgress(false)}
        pendingCount={syncState.pendingCount}
        isRunning={syncState.isRunning}
      />
    </>
  );
};

export default OfflineIndicator;